import React, { forwardRef, useState } from 'react';
import {
  View,
  TextInput,
  Text,
  StyleSheet,
  TextInputProps,
  ViewStyle,
  TouchableOpacity,
} from 'react-native';
import { X, type LucideIcon } from 'lucide-react-native';
import { useTheme, TYPOGRAPHY, SPACING, BORDER_RADIUS, withWeight } from '../../theme';
import { useWebHover } from '../../hooks/useWebHover';

interface InputProps extends TextInputProps {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: LucideIcon;
  // When provided, renders a clear (X) button on the right while the field
  // has text. Switches the field to the bordered-row layout.
  onClear?: () => void;
  containerStyle?: ViewStyle;
  /** Panel-density field (Sept 28): a small label and a 36px input, as in
   *  the Assign form. For the plain field only (no leftIcon or onClear). */
  compact?: boolean;
}

export const Input = forwardRef<TextInput, InputProps>(function Input(
  {
    label,
    error,
    hint,
    leftIcon: LeftIcon,
    onClear,
    containerStyle,
    compact = false,
    style,
    accessibilityLabel,
    accessibilityHint,
    ...props
  },
  ref
) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const { hovered, hoverProps } = useWebHover(props.editable !== false);

  const borderColor = error
    ? colors.danger
    : isFocused
      ? colors.borderFocus
      : hovered
        ? colors.textTertiary
        : colors.border;

  // Default accessibility wiring — call sites can still override with their
  // own accessibilityLabel/Hint props if they need different copy than the
  // visible label.
  const a11yLabel = accessibilityLabel ?? label;
  const a11yHint = accessibilityHint ?? hint;

  return (
    <View
      style={[styles.container, compact && styles.containerCompact, containerStyle]}
      // @ts-expect-error dataSet is a react-native-web prop (tells
      // audit-design-consistency.mjs which field mode this is); not in RN's
      // type defs.
      dataSet={{ rediInput: compact ? 'compact' : 'default' }}>
      {label && (
        <Text
          style={[styles.label, compact && styles.labelCompact, { color: colors.textSecondary }]}>
          {label}
        </Text>
      )}
      {LeftIcon ? (
        <View
          {...hoverProps}
          style={[
            styles.inputRow,
            {
              // `background`, not `surface`: on a page the fill vanishes and
              // the hairline is the field (the mockup's "plain bordered
              // inputs"); over the map it still reads as opaque.
              backgroundColor: colors.background,
              borderColor,
              borderRadius: BORDER_RADIUS.lg,
              borderWidth: 1,
            },
          ]}>
          <LeftIcon
            size={18}
            color={colors.textTertiary}
            style={{ marginLeft: 14, marginRight: 8 }}
          />
          <TextInput
            ref={ref}
            style={[
              styles.input,
              styles.inputFlex,
              {
                backgroundColor: 'transparent',
                borderWidth: 0,
                color: colors.text,
              },
              style,
            ]}
            placeholderTextColor={colors.textTertiary}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            accessibilityLabel={a11yLabel}
            accessibilityHint={a11yHint}
            {...props}
          />
        </View>
      ) : onClear ? (
        <View
          style={[
            styles.inputRow,
            {
              // `background`, not `surface`: on a page the fill vanishes and
              // the hairline is the field (the mockup's "plain bordered
              // inputs"); over the map it still reads as opaque.
              backgroundColor: colors.background,
              borderColor,
              borderRadius: BORDER_RADIUS.lg,
              borderWidth: 1,
            },
          ]}>
          <TextInput
            ref={ref}
            {...hoverProps}
            // Keep styles.input's horizontal padding (no leftIcon here, so don't
            // borrow inputFlex's paddingLeft:0 — that left the text flush left).
            style={[
              styles.input,
              styles.clearInputFlex,
              { backgroundColor: 'transparent', borderWidth: 0, color: colors.text },
              style,
            ]}
            placeholderTextColor={colors.textTertiary}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            accessibilityLabel={a11yLabel}
            accessibilityHint={a11yHint}
            {...props}
          />
          {!!props.value && (
            <TouchableOpacity
              onPress={onClear}
              accessibilityRole="button"
              accessibilityLabel="Clear text"
              hitSlop={8}
              style={styles.clearButton}>
              <X size={16} color={colors.textTertiary} />
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <TextInput
          ref={ref}
          {...hoverProps}
          style={[
            styles.input,
            compact && styles.inputCompact,
            {
              backgroundColor: colors.background,
              borderColor,
              color: colors.text,
            },
            style,
          ]}
          placeholderTextColor={colors.textTertiary}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          accessibilityLabel={a11yLabel}
          accessibilityHint={a11yHint}
          {...props}
        />
      )}
      {error && (
        // accessibilityLiveRegion (Android) + the `alert` role get the new
        // error read out by screen readers as soon as it appears, instead of
        // waiting for the user to refocus the input.
        <Text
          style={[styles.error, { color: colors.danger }]}
          accessibilityLiveRegion="polite"
          accessibilityRole="alert">
          {error}
        </Text>
      )}
      {/* Hints are read, so they need AA: textTertiary is sub-AA by design and
          belongs to placeholders and disabled text. */}
      {hint && !error && <Text style={[styles.hint, { color: colors.textSecondary }]}>{hint}</Text>}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginBottom: SPACING.md,
  },
  containerCompact: {
    marginBottom: SPACING.smd,
  },
  labelCompact: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
    marginBottom: SPACING.xs,
  },
  inputCompact: {
    ...TYPOGRAPHY.bodySmall,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.smd,
    borderRadius: BORDER_RADIUS.sm,
  },
  label: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  input: {
    ...TYPOGRAPHY.body,
    paddingVertical: 14,
    paddingHorizontal: SPACING.md,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
  },
  error: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 6,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 6,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inputFlex: {
    flex: 1,
    paddingLeft: 0,
  },
  // Like inputFlex but keeps the field's left padding (used by the clear-button
  // layout, which has no left icon to provide the inset).
  clearInputFlex: {
    flex: 1,
  },
  clearButton: {
    paddingHorizontal: SPACING.md,
  },
});
